import React, { useEffect, useState, useCallback } from 'react';
import {
    Modal, Table, Button, Form, Input, Switch, Spin, Alert,
    Popconfirm, ConfigProvider, TimePicker, Space, Radio, DatePicker, Tag,
    ColorPicker, Checkbox,
} from 'antd';
import {
    EditOutlined, DeleteOutlined, PlusOutlined, ArrowLeftOutlined,
    MinusCircleOutlined, QuestionCircleOutlined, ExclamationCircleOutlined,
    TagsOutlined, CalendarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import axios from 'axios';
import { Config } from '../Common/Config';
import './SpecialPricingBandsModal.css';

const { RangePicker } = DatePicker;

// en-gb starts the week on Monday (matches DAYS_OF_WEEK's Mon=1..Sun=7
// convention below) — without this, antd's calendar defaults to Sunday-first.
// dayjs.locale() is global/module-level, same pattern already established in
// ClinicCalendarPage.js.
dayjs.locale('en-gb');

/* ─────────────────────────────────────────────────────────────────────
   DAYS OF WEEK CONSTANTS
   ───────────────────────────────────────────────────────────────────── */
const DAYS_OF_WEEK = [
    { label: 'Mon', value: 1 },
    { label: 'Tue', value: 2 },
    { label: 'Wed', value: 3 },
    { label: 'Thu', value: 4 },
    { label: 'Fri', value: 5 },
    { label: 'Sat', value: 6 },
    { label: 'Sun', value: 7 },
];

/* ─────────────────────────────────────────────────────────────────────
   API helpers
   Each band is scoped to one duration_id.
   schedule_type: 'days' | 'dates' | 'date_range'
   days_of_week: number[]   (1=Mon … 7=Sun)              — 'days' only
   custom_dates: string[]   ('YYYY-MM-DD')                — 'dates' only
   from_date / to_date: string | null ('YYYY-MM-DD')      — 'date_range' only, both inclusive
   is_weekend: boolean (default false) — when true, every day referenced by
   the schedule above must be Sat/Sun (for 'date_range', just the from_date/
   to_date boundaries); when false (default), every day must be Mon-Fri.
   price: single price for this duration
   ───────────────────────────────────────────────────────────────────── */
const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
});

/**
 * Parses the backend's error envelope. Confirmed shape on validation/business
 * failures is `{ success:false, message, error: { <field>: ["<msg>"] } }`
 * (singular `error`, not the Laravel-default plural `errors`) — but we fall
 * back to the plural key too in case plain field validation ever uses the
 * framework default instead of the documented custom shape.
 */
const extractApiErrorMessage = (err) => {
    const errorObj = err?.response?.data?.error;
    if (errorObj && typeof errorObj === 'object' && !Array.isArray(errorObj)) {
        const allMessages = Object.values(errorObj).flat().filter(Boolean);
        if (allMessages.length) {
            return {
                message: allMessages[0],
                isRangesError: Object.prototype.hasOwnProperty.call(errorObj, 'ranges'),
            };
        }
    }
    const errorsObj = err?.response?.data?.errors;
    if (errorsObj && typeof errorsObj === 'object') {
        const allMessages = Object.values(errorsObj).flat().filter(Boolean);
        if (allMessages.length) {
            return {
                message: allMessages[0],
                isRangesError: Object.prototype.hasOwnProperty.call(errorsObj, 'ranges'),
            };
        }
    }
    return {
        message: err?.response?.data?.message || err?.message || 'Something went wrong.',
        isRangesError: false,
    };
};

/* ─────────────────────────────────────────────────────────────────────
   Colour swatches
   ───────────────────────────────────────────────────────────────────── */
const COLOUR_SWATCHES = [
    '#008385', '#ff9900', '#DC2626', '#660099', '#00A3A6', '#2563EB',
];

/* ─────────────────────────────────────────────────────────────────────
   Time helpers
   ───────────────────────────────────────────────────────────────────── */
const timeToMinutes = (t) => {
    if (!t) return null;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
};

const getDurationMinutes = (id) => {
    switch (Number(id)) {
        case 1: return 15;
        case 2: return 30;
        case 3: return 45;
        case 4: return 60;
        default: return 15;
    }
};

/** disabledTime for START TimePicker: minimum 08:00, max = evening - duration */
const makeStartDisabledTime = (eveningBoundary, durationId) => () => {
    const duration = getDurationMinutes(durationId);
    let maxStartMins = null;
    if (eveningBoundary && duration > 0) {
        maxStartMins = timeToMinutes(eveningBoundary.slice(0, 5)) - duration;
    }

    return {
        disabledHours: () => {
            const disabled = Array.from({ length: 8 }, (_, i) => i); // 0-7
            if (maxStartMins !== null) {
                const maxHour = Math.floor(maxStartMins / 60);
                for (let i = maxHour + 1; i < 24; i++) {
                    disabled.push(i);
                }
            }
            return disabled;
        },
        disabledMinutes: (selectedHour) => {
            if (maxStartMins !== null) {
                const maxHour = Math.floor(maxStartMins / 60);
                if (selectedHour === maxHour) {
                    const maxMinute = maxStartMins % 60;
                    return Array.from({ length: 59 - maxMinute }, (_, i) => maxMinute + 1 + i);
                }
            }
            return [];
        }
    };
};

/** disabledTime for END TimePicker: maximum = evening_price_starts_at */
const makeEndDisabledTime = (eveningBoundary) => () => {
    if (!eveningBoundary) return {};
    const [bH, bM] = eveningBoundary.slice(0, 5).split(':').map(Number);
    return {
        disabledHours: () => Array.from({ length: 23 - bH }, (_, i) => bH + 1 + i),
        disabledMinutes: (selectedHour) => {
            if (selectedHour === bH) {
                // Allow only minutes <= bM
                return Array.from({ length: 59 - bM }, (_, i) => bM + 1 + i);
            }
            return [];
        },
    };
};

/** Validate time ranges */
const validateTimeRanges = (timeRanges, eveningBoundary, durationId) => {
    const errors = timeRanges.map(() => null);
    let valid = true;
    const boundaryMins = eveningBoundary ? timeToMinutes(eveningBoundary.slice(0, 5)) : null;
    const minStartMins = timeToMinutes('08:00');
    const duration = getDurationMinutes(durationId);

    timeRanges.forEach((range, i) => {
        if (!range.start_time) { errors[i] = 'Start time is required.'; valid = false; return; }
        if (!range.end_time)   { errors[i] = 'End time is required.';   valid = false; return; }

        const startM = timeToMinutes(range.start_time);
        const endM   = timeToMinutes(range.end_time);

        if (startM < minStartMins) {
            errors[i] = 'Start time must be 08:00 or later.'; valid = false; return;
        }
        if (boundaryMins !== null && duration > 0 && startM > boundaryMins - duration) {
            const maxH = Math.floor((boundaryMins - duration) / 60).toString().padStart(2, '0');
            const maxM = ((boundaryMins - duration) % 60).toString().padStart(2, '0');
            errors[i] = `Start time must be at or before ${maxH}:${maxM} to fit ${duration}m before the Day-Time boundary.`;
            valid = false; return;
        }
        if (endM <= startM) {
            errors[i] = 'End time must be after start time.'; valid = false; return;
        }
        if (boundaryMins !== null && endM > boundaryMins) {
            errors[i] = `End time must be at or before ${eveningBoundary.slice(0, 5)} (Day-Time boundary).`;
            valid = false; return;
        }
        // Overlap check
        timeRanges.forEach((other, j) => {
            if (i === j || !other.start_time || !other.end_time) return;
            const oStart = timeToMinutes(other.start_time);
            const oEnd   = timeToMinutes(other.end_time);
            if (startM < oEnd && endM > oStart) {
                errors[i] = 'This range overlaps with another range.';
                valid = false;
            }
        });
    });

    return { errors, valid };
};

/**
 * JS Date.day() is Sun=0..Sat=6; the backend/UI convention here is
 * ISO weekday Mon=1..Sun=7 (see DAYS_OF_WEEK above).
 */
const toIsoWeekday = (dateStr) => (((dayjs(dateStr).day() + 6) % 7) + 1);

/** Past-dates-only check, shared by every schedule picker below. */
const isPastDate = (current) => !!current && current < dayjs().startOf('day');

/** True for Saturday/Sunday (dayjs .day() is Sun=0..Sat=6). */
const isWeekendDate = (current) => current.day() === 0 || current.day() === 6;

/** disabledDate for the Custom Dates picker and the Date Range picker: past
 * dates are always disabled, plus whichever of weekday/weekend doesn't match
 * the band's `is_weekend` setting — the backend now enforces this (every
 * days_of_week/custom_dates entry, or the from_date/to_date boundaries for
 * a date_range band, must be weekend-only or weekday-only, never mixed). */
const buildScheduleDisabledDate = (weekendOnly) => (current) => {
    if (isPastDate(current)) return true;
    return weekendOnly ? !isWeekendDate(current) : isWeekendDate(current);
};

/**
 * Best-effort, NON-authoritative client-side pre-check for a cross-band
 * schedule/time conflict, using the bands already loaded for this bucket +
 * duration. This exists purely to give the admin instant feedback before a
 * round trip — it must only ever block on a *proven* conflict. The backend
 * remains the source of truth and its 422 response is always shown too.
 */
// NOTE: 'date_range' bands are intentionally NOT weekday-approximated here —
// draft.selectedDates / other.custom_dates are both empty for that schedule
// type, so candidateDays/otherDays resolve to [] and this pre-check silently
// no-ops for them (never flags, never blocks) rather than guessing via an
// imprecise weekday expansion. Consistent with this helper's "only ever
// block on a *proven* conflict" contract — the backend's 422 is the real
// check for date_range overlaps.
//
// No separate is_weekend handling is needed here either: the UI only ever
// lets selectedDays/selectedDates contain values matching the band's own
// is_weekend setting (6/7 vs 1-5, or weekend vs weekday literal dates), so a
// weekend-only band and a weekday-only band never intersect on the ISO
// weekday axis below — exactly matching the backend's "different is_weekend
// values can never conflict" rule (see requirements doc Scenario 14).
const detectClientSideOverlap = (draft, existingBands, excludeId) => {
    const candidateDays = draft.scheduleType === 'days'
        ? draft.selectedDays
        : draft.selectedDates.map(toIsoWeekday);

    for (const other of existingBands) {
        if (other.id === excludeId) continue;
        if (!other.is_active) continue; // conservative: only block on live conflicts

        const otherDays = other.schedule_type === 'days'
            ? (other.days_of_week || [])
            : (other.custom_dates || []).map(toIsoWeekday);

        const daysIntersect = candidateDays.some((d) => otherDays.includes(d));
        if (!daysIntersect) continue;

        // Two 'dates' schedules should only conflict on a literal shared date,
        // not merely the same resolved weekday.
        if (draft.scheduleType === 'dates' && other.schedule_type === 'dates') {
            const literalOverlap = draft.selectedDates.some((d) => (other.custom_dates || []).includes(d));
            if (!literalOverlap) continue;
        }

        const timeOverlap = draft.timeRanges.some((r) => {
            const rStart = timeToMinutes(r.start_time);
            const rEnd = timeToMinutes(r.end_time);
            return (other.ranges || []).some((o) => {
                const oStart = timeToMinutes(o.start_time.slice(0, 5));
                const oEnd = timeToMinutes(o.end_time.slice(0, 5));
                return rStart < oEnd && rEnd > oStart;
            });
        });
        if (timeOverlap) return other;
    }
    return null;
};

/* ─────────────────────────────────────────────────────────────────────
   Main Component
   ───────────────────────────────────────────────────────────────────── */
const SpecialPricingBandsModal = ({
    visible,
    onClose,
    bucket,
    durationId,
    durationLabel,
    normalPrice,
    messageApi,
    onBandsChanged,
}) => {
    const [form] = Form.useForm();
    // Modal.confirm() (the static/imperative API) doesn't reliably mount
    // under this app's React/antd setup — confirmed both in this suite and
    // live in the browser (SPB-CRUD-027): the "Final Warning" dialog never
    // appears, with no error. Modal.useModal() renders its confirm dialogs
    // through the normal React tree instead of a detached static root, so
    // use that here.
    const [modalApi, modalContextHolder] = Modal.useModal();

    // List state
    const [bands, setBands]             = useState([]);
    const [loading, setLoading]         = useState(false);

    // Form mode
    const [formMode, setFormMode]       = useState('list');
    const [editingBand, setEditingBand] = useState(null);
    const [saving, setSaving]           = useState(false);
    const [deleting, setDeleting]       = useState(false);
    const [togglingId, setTogglingId]   = useState(null);

    // Time range state
    const [timeRanges, setTimeRanges]           = useState([{ start_time: null, end_time: null }]);
    const [timeRangeErrors, setTimeRangeErrors] = useState([]);

    // Colour watch
    const [colourWatch, setColourWatch] = useState('#008385');

    // Schedule state
    const [scheduleType, setScheduleType]       = useState('days');      // 'days' | 'dates' | 'date_range'
    const [isWeekend, setIsWeekend]             = useState(false); // false = weekday-only (default), true = weekend-only
    const [selectedDays, setSelectedDays]       = useState([1, 2, 3, 4, 5]); // Mon–Fri by default
    const [selectedDates, setSelectedDates]     = useState([]);
    const [datePickerValue, setDatePickerValue] = useState(null);
    const [scheduleDateRange, setScheduleDateRange] = useState(null); // [dayjs, dayjs] | null — 'date_range' mode only
    const [scheduleError, setScheduleError]     = useState(null);

    /* ── Memoised end-time disabler for this bucket ─────────────────── */
    const endDisabledTime = makeEndDisabledTime(bucket?.evening_price_starts_at);
    const startDisabledTime = makeStartDisabledTime(bucket?.evening_price_starts_at, durationId);

    /* ── Load bands ─────────────────────────────────────────────────── */
    const loadBands = useCallback(async () => {
        if (!bucket?.id || !durationId) return;
        setLoading(true);
        try {
            const response = await axios.get(
                `${Config.url}clinic-bucket-pricing/${bucket.id}/special-pricing-bands?duration_id=${durationId}`,
                { headers: authHeaders() }
            );
            setBands(response?.data?.data || []);
        } catch {
            messageApi.error({ content: 'Failed to load special pricing bands.' });
        } finally {
            setLoading(false);
        }
    }, [bucket, durationId, messageApi]);

    useEffect(() => {
        if (visible && bucket?.id && durationId) {
            setFormMode('list');
            loadBands();
        }
    }, [visible, bucket, durationId, loadBands]);

    /* ── Reset ──────────────────────────────────────────────────────── */
    const resetFormState = () => {
        form.resetFields();
        setTimeRanges([{ start_time: null, end_time: null }]);
        setTimeRangeErrors([]);
        setColourWatch('#008385');
        setEditingBand(null);
        setScheduleType('days');
        setIsWeekend(false);
        setSelectedDays([1, 2, 3, 4, 5]);
        setSelectedDates([]);
        setDatePickerValue(null);
        setScheduleDateRange(null);
        setScheduleError(null);
    };

    const goToList = () => { resetFormState(); setFormMode('list'); };

    /* ── Open add form ──────────────────────────────────────────────── */
    const openAddForm = () => {
        resetFormState();
        form.setFieldsValue({ colour: '#008385', is_active: true });
        setColourWatch('#008385');
        setFormMode('add');
    };

    /* ── Open edit form ─────────────────────────────────────────────── */
    const openEditForm = (band) => {
        resetFormState();
        setEditingBand(band);
        form.setFieldsValue({
            band_name:   band.band_name,
            description: band.description,
            colour:      band.colour,
            is_active:   band.is_active,
            price:       String(band.price),
        });
        setColourWatch(band.colour);
        setTimeRanges(band.ranges.map((r) => ({
            start_time: r.start_time.slice(0, 5),
            end_time:   r.end_time.slice(0, 5),
        })));
        // Restore schedule
        const sType = band.schedule_type || 'days';
        const bandIsWeekend = !!band.is_weekend;
        setScheduleType(sType);
        setIsWeekend(bandIsWeekend);
        if (sType === 'days') {
            setSelectedDays(band.days_of_week || (bandIsWeekend ? [6, 7] : [1, 2, 3, 4, 5]));
        } else if (sType === 'date_range') {
            setScheduleDateRange(
                band.from_date && band.to_date ? [dayjs(band.from_date), dayjs(band.to_date)] : null
            );
        } else {
            setSelectedDates(band.custom_dates || []);
        }
        setFormMode('edit');
    };

    /* ── Time range helpers ─────────────────────────────────────────── */
    const addTimeRange    = () => setTimeRanges((p) => [...p, { start_time: null, end_time: null }]);
    const removeTimeRange = (i) => {
        setTimeRanges((p) => p.filter((_, idx) => idx !== i));
        setTimeRangeErrors((p) => p.filter((_, idx) => idx !== i));
    };
    const updateTimeRange = (i, field, val) =>
        setTimeRanges((p) => { const u = [...p]; u[i] = { ...u[i], [field]: val }; return u; });

    /* ── Day toggle ─────────────────────────────────────────────────── */
    const toggleDay = (val) => {
        setSelectedDays((prev) =>
            prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
        );
        setScheduleError(null);
    };

    /* ── Weekend-only toggle ────────────────────────────────────────────
       Switching this changes which days/dates are even selectable, so any
       existing selection is cleared rather than silently left holding
       values that would now violate the new constraint (weekday-only vs
       weekend-only can never be mixed — see requirements doc's is_weekend
       section). Days reset to a sensible default for the new mode, same as
       the initial useState default. */
    const handleWeekendToggle = (e) => {
        const nextWeekend = e.target.checked;
        setIsWeekend(nextWeekend);
        setSelectedDays(nextWeekend ? [6, 7] : [1, 2, 3, 4, 5]);
        setSelectedDates([]);
        setScheduleDateRange(null);
        setScheduleError(null);
    };

    /* ── Save ───────────────────────────────────────────────────────── */
    const handleSave = async () => {
        // 1. Ant form validation
        let values;
        try { values = await form.validateFields(); } catch { return; }

        // 2. Schedule validation
        if (scheduleType === 'days' && selectedDays.length === 0) {
            setScheduleError('Please select at least one day.');
            return;
        }
        if (scheduleType === 'dates' && selectedDates.length === 0) {
            setScheduleError('Please add at least one date.');
            return;
        }
        if (scheduleType === 'date_range' && (!scheduleDateRange?.[0] || !scheduleDateRange?.[1])) {
            setScheduleError('Please select a date range.');
            return;
        }
        setScheduleError(null);

        // 3. Time range validation
        const { errors, valid } = validateTimeRanges(timeRanges, bucket?.evening_price_starts_at, durationId);
        setTimeRangeErrors(errors);
        if (!valid) return;

        // 4. Best-effort cross-band overlap pre-check (non-authoritative — see helper docblock)
        const conflict = detectClientSideOverlap(
            { scheduleType, selectedDays, selectedDates, timeRanges },
            bands,
            editingBand?.id
        );
        if (conflict) {
            setTimeRangeErrors(timeRanges.map((_, i) =>
                i === 0 ? `This overlaps with existing band "${conflict.band_name}". Adjust the time or schedule.` : null
            ));
            return;
        }

        // 5. Best-effort band-name uniqueness pre-check — advisory only, never blocks
        const trimmedName = values.band_name?.trim().toLowerCase();
        const duplicate = bands.find(
            (b) => b.id !== editingBand?.id && b.band_name?.trim().toLowerCase() === trimmedName
        );
        if (duplicate) {
            messageApi.warning({
                content: `Heads up: another band for ${durationLabel} is already named "${duplicate.band_name}".`,
            });
        }

        const requestBody = {
            clinic_price_bucket_id: bucket.id,
            duration_id: durationId,
            band_name:     values.band_name?.trim(),
            description:   values.description?.trim(),
            colour:        values.colour,
            is_active:     values.is_active ?? true,
            price:         Number(parseFloat(values.price).toFixed(2)),
            schedule_type: scheduleType,
            is_weekend:    isWeekend,
            days_of_week:  scheduleType === 'days'  ? [...selectedDays].sort() : [],
            custom_dates:  scheduleType === 'dates' ? [...selectedDates].sort() : [],
            from_date: scheduleType === 'date_range' ? scheduleDateRange[0].format('YYYY-MM-DD') : null,
            to_date:   scheduleType === 'date_range' ? scheduleDateRange[1].format('YYYY-MM-DD') : null,
            ranges: timeRanges.map((r) => ({ start_time: r.start_time, end_time: r.end_time })),
        };

        setSaving(true);
        try {
            if (editingBand) {
                await axios.put(
                    `${Config.url}special-pricing-bands/${editingBand.id}`,
                    requestBody,
                    { headers: authHeaders() }
                );
                messageApi.success({ content: 'Band updated successfully.' });
            } else {
                await axios.post(
                    `${Config.url}special-pricing-bands`,
                    requestBody,
                    { headers: authHeaders() }
                );
                messageApi.success({ content: 'Band created successfully.' });
            }
            goToList();
            loadBands();
            onBandsChanged?.();
        } catch (err) {
            const { message: errMsg, isRangesError } = extractApiErrorMessage(err);
            if (isRangesError) {
                setTimeRangeErrors(timeRanges.map((_, i) => (i === 0 ? errMsg : null)));
            } else {
                messageApi.error({ content: errMsg });
            }
        } finally {
            setSaving(false);
        }
    };

    /* ── Toggle active ──────────────────────────────────────────────── */
    const toggleActive = async (band, value) => {
        setTogglingId(band.id);
        try {
            // Sending ONLY { is_active } hits the backend's fast partial-update
            // path, which bypasses the heavy schedule/overlap validation.
            await axios.put(
                `${Config.url}special-pricing-bands/${band.id}`,
                { is_active: value },
                { headers: authHeaders() }
            );
            await loadBands();
            onBandsChanged?.();
        } catch (err) {
            const { message: errMsg } = extractApiErrorMessage(err);
            messageApi.error({ content: errMsg || 'Failed to update band status.' });
        } finally {
            setTogglingId(null);
        }
    };

    /* ── Delete: step 2 ─────────────────────────────────────────────── */
    const executeDelete = async (band) => {
        setDeleting(true);
        try {
            await axios.delete(
                `${Config.url}special-pricing-bands/${band.id}`,
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            messageApi.success({ content: 'Band deleted successfully.' });
            await loadBands();
            onBandsChanged?.();
        } catch (err) {
            const { message: errMsg } = extractApiErrorMessage(err);
            messageApi.error({ content: errMsg || 'Failed to delete band.' });
        } finally {
            setDeleting(false);
        }
    };

    /* ── Delete: step 1 ─────────────────────────────────────────────── */
    const handleFirstDeleteConfirm = (band) => {
        modalApi.confirm({
            title: 'Final Warning',
            icon: <ExclamationCircleOutlined style={{ color: '#dc2626' }} />,
            content: (
                <span>
                    You are permanently deleting <strong>"{band.band_name}"</strong> for{' '}
                    <strong>{durationLabel}</strong>. This cannot be undone.
                </span>
            ),
            okText: 'Cancel',
            cancelText: 'Yes, Delete Permanently',
            okButtonProps: { className: 'confirmation-popup-ok-button' },
            cancelButtonProps: { style: { background: '#dc2626', color: '#fff', border: 'none' } },
            onCancel: () => executeDelete(band),
        });
    };

    /* ── Schedule summary label (for list table) ────────────────────── */
    const renderScheduleSummary = (record) => {
        if (record.schedule_type === 'dates') {
            const count = record.custom_dates?.length || 0;
            return (
                <div style={{ fontSize: 11 }}>
                    <CalendarOutlined style={{ color: '#008385', marginRight: 4 }} />
                    <span style={{ color: '#008385', fontWeight: 600 }}>{count} custom date{count !== 1 ? 's' : ''}</span>
                    {count > 0 && (
                        <div style={{ color: '#8c8c8c', marginTop: 2 }}>
                            {record.custom_dates.slice(0, 2).map((d) => dayjs(d).format('DD MMM YY')).join(', ')}
                            {count > 2 ? ` +${count - 2} more` : ''}
                        </div>
                    )}
                </div>
            );
        }
        if (record.schedule_type === 'date_range') {
            return (
                <div style={{ fontSize: 11 }}>
                    <CalendarOutlined style={{ color: '#008385', marginRight: 4 }} />
                    <span style={{ color: '#008385', fontWeight: 600 }}>
                        {dayjs(record.from_date).format('D MMM YYYY')} – {dayjs(record.to_date).format('D MMM YYYY')}
                    </span>
                </div>
            );
        }
        const days = record.days_of_week || [];
        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {DAYS_OF_WEEK.filter((d) => days.includes(d.value)).map((d) => (
                    <span key={d.value} style={{
                        padding: '1px 6px', borderRadius: 10,
                        fontSize: 10, fontWeight: 600,
                        background: '#f0fafa', border: '1px solid #008385', color: '#008385',
                    }}>{d.label}</span>
                ))}
            </div>
        );
    };

    /* ── Band list table columns ────────────────────────────────────── */
    const listColumns = [
        {
            title: 'Band Name',
            key: 'band_name',
            render: (_, record) => (
                <div>
                    <div className="band-name-cell" style={{ borderLeftColor: record.colour }}>
                        {record.band_name}
                    </div>
                    <div className="band-description-cell">{record.description}</div>
                </div>
            ),
        },
        {
            title: 'Special Price',
            key: 'price',
            width: 110,
            render: (_, record) => (
                <span style={{
                    background: record.colour + '22',
                    color: record.colour,
                    border: `1px solid ${record.colour}55`,
                    borderRadius: 8, padding: '3px 10px',
                    fontWeight: 700, fontSize: 13,
                }}>
                    £{parseFloat(record.price).toFixed(2)}
                </span>
            ),
        },
        {
            title: 'Schedule',
            key: 'schedule',
            width: 150,
            render: (_, record) => renderScheduleSummary(record),
        },
        {
            title: 'Time Ranges',
            key: 'ranges',
            width: 150,
            render: (_, record) => (
                <div style={{ fontSize: 12 }}>
                    {record.ranges.map((r, i) => (
                        <div key={i} style={{ marginBottom: 3 }}>
                            <span style={{
                                background: record.colour + '22',
                                color: record.colour,
                                border: `1px solid ${record.colour}55`,
                                borderRadius: 6, padding: '1px 7px',
                                fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap',
                            }}>
                                {r.start_time} – {r.end_time}
                            </span>
                        </div>
                    ))}
                </div>
            ),
        },
        {
            title: 'Status',
            key: 'is_active',
            width: 85,
            render: (_, record) => (
                <ConfigProvider theme={{ components: { Switch: { colorPrimary: '#008385', colorPrimaryHover: '#006f71' } } }}>
                    <Switch
                        checked={record.is_active}
                        loading={togglingId === record.id}
                        disabled={togglingId !== null && togglingId !== record.id}
                        onChange={(val) => toggleActive(record, val)}
                        checkedChildren="On"
                        unCheckedChildren="Off"
                    />
                </ConfigProvider>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 75,
            render: (_, record) => (
                <Space size={10}>
                    <EditOutlined
                        style={{ color: '#008385', cursor: 'pointer', fontSize: 16 }}
                        onClick={() => openEditForm(record)}
                    />
                    <Popconfirm
                        title="Delete Special Pricing Band"
                        description={`Are you sure you want to delete "${record.band_name}"?`}
                        okText="No"
                        cancelText="Yes, Delete"
                        okButtonProps={{ className: 'confirmation-popup-ok-button' }}
                        cancelButtonProps={{ className: 'confirmation-popup-cancel-button' }}
                        icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                        onCancel={() => handleFirstDeleteConfirm(record)}
                    >
                        <DeleteOutlined style={{ color: '#dc2626', cursor: 'pointer', fontSize: 16 }} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    /* ── Render: list ───────────────────────────────────────────────── */
    const renderList = () => (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#1f1f1f' }}>Special Pricing Bands</div>
                    <div style={{ fontSize: 12, color: '#5a9a9b', marginTop: 2 }}>
                        <strong style={{ color: '#008385' }}>{durationLabel}</strong>
                        {' '}— {bucket?.bucket_name}
                        {' '}— Day-Time boundary: <strong>{bucket?.evening_price_starts_at?.slice(0, 5) || 'N/A'}</strong>
                    </div>
                </div>
                <Button className="add-generic-btn" icon={<PlusOutlined />} onClick={openAddForm}>
                    Add Band
                </Button>
            </div>

            {loading ? (
                <ConfigProvider theme={{ token: { colorPrimary: '#008385' } }}>
                    <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
                </ConfigProvider>
            ) : bands.length === 0 ? (
                <div className="special-pricing-empty-state">
                    <TagsOutlined style={{ fontSize: 40, color: '#c5e8e8', marginBottom: 12 }} />
                    <p>No special pricing bands configured for <strong>{durationLabel}</strong>.</p>
                    <Button className="add-generic-btn" icon={<PlusOutlined />} onClick={openAddForm}>
                        Add Special Pricing Band
                    </Button>
                </div>
            ) : (
                <ConfigProvider theme={{ token: { colorPrimary: '#008385' }, components: { Table: { headerBg: '#008385', headerColor: '#fff' } } }}>
                    <Table
                        className="band-list-table"
                        dataSource={bands}
                        columns={listColumns}
                        rowKey="id"
                        pagination={false}
                        bordered
                        size="small"
                        loading={deleting}
                    />
                </ConfigProvider>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                <Button className="add-generic-btn" onClick={onClose}>
                    Close
                </Button>
            </div>
        </>
    );

    /* ── Render: form (add / edit) ──────────────────────────────────── */
    const renderForm = () => (
        <>
            {/* Back */}
            <div
                className="spb-back-link"
                onClick={() => { if (!saving) goToList(); }}
                style={saving ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
                <ArrowLeftOutlined style={{ fontSize: 13 }} /> Back to list
            </div>

            {/* Duration badge */}
            <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: '#f0fafa', border: '1px solid #c5e8e8',
                borderRadius: 20, padding: '4px 14px', marginBottom: 16, fontSize: 13,
            }}>
                <TagsOutlined style={{ color: '#008385' }} />
                <span style={{ fontWeight: 600, color: '#008385' }}>{durationLabel}</span>
            </div>

            <div style={{ fontWeight: 700, fontSize: 15, color: '#1f1f1f', marginBottom: 20 }}>
                {formMode === 'edit' ? `Edit: ${editingBand?.band_name}` : 'Add Special Pricing Band'}
            </div>

            <Form form={form} layout="vertical" autoComplete="off">

                {/* ── Band Name ── */}
                <Form.Item
                    name="band_name"
                    label="Band Name"
                    rules={[
                        { required: true, message: 'Band Name is required.' },
                        { max: 255, message: 'Maximum 255 characters.' },
                    ]}
                >
                    <Input placeholder="e.g. Quiet Hours Special" />
                </Form.Item>

                {/* ── Description ── */}
                <Form.Item
                    name="description"
                    label="Description"
                    rules={[
                        { max: 1000, message: 'Maximum 1000 characters.' },
                    ]}
                >
                    <Input.TextArea rows={2} placeholder="e.g. Book during our quieter periods and save!" />
                </Form.Item>

                {/* ── Special Price ── */}
                <Form.Item label={<span style={{ fontWeight: 600 }}>Special Price (£) — {durationLabel}</span>}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 16, color: '#4a4a4a', fontWeight: 600 }}>£</span>
                        <Form.Item
                            name="price"
                            noStyle
                            rules={[
                                { required: true, message: 'Price is required.' },
                                { pattern: /^\d+(\.\d{1,2})?$/, message: 'e.g. 35 or 35.50' },
                                {
                                    validator: (_, value) => {
                                        if (value && normalPrice && parseFloat(value) >= parseFloat(normalPrice)) {
                                            return Promise.reject(`Must be less than normal price (£${normalPrice})`);
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <Input
                                style={{ width: 120, background: '#f0fdf4', textAlign: 'center', fontWeight: 600 }}
                                placeholder="0.00"
                            />
                        </Form.Item>
                        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                            Applies to <strong>{durationLabel}</strong> appointments only
                        </span>
                    </div>
                </Form.Item>

                {/* ── Colour ── */}
                <Form.Item label="Band Colour">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                        <ConfigProvider theme={{ token: { colorPrimary: '#008385' } }}>
                            <ColorPicker
                                value={colourWatch || '#008385'}
                                onChange={(color) => {
                                    const hex = color.toHexString();
                                    form.setFieldValue('colour', hex);
                                    setColourWatch(hex);
                                }}
                                size="large"
                            />
                        </ConfigProvider>
                        <Form.Item
                            name="colour"
                            noStyle
                            rules={[
                                { required: true, message: 'Colour is required.' },
                                { pattern: /^#[0-9A-Fa-f]{6}$/, message: 'Enter a valid hex colour (e.g. #E85D04).' },
                            ]}
                        >
                            <Input
                                style={{ width: 130 }}
                                placeholder="#008385"
                                onChange={(e) => setColourWatch(e.target.value)}
                            />
                        </Form.Item>
                    </div>
                    <div className="band-colour-swatch-row">
                        {COLOUR_SWATCHES.map((c) => (
                            <div
                                key={c}
                                className={`band-colour-swatch${colourWatch === c ? ' active' : ''}`}
                                style={{ background: c }}
                                onClick={() => { form.setFieldValue('colour', c); setColourWatch(c); }}
                                title={c}
                            />
                        ))}
                    </div>
                </Form.Item>

                {/* ── Active ── */}
                <ConfigProvider theme={{ components: { Switch: { colorPrimary: '#008385', colorPrimaryHover: '#006f71' } } }}>
                    <Form.Item name="is_active" label="Active" valuePropName="checked">
                        <Switch checkedChildren="Yes" unCheckedChildren="No" />
                    </Form.Item>
                </ConfigProvider>

                {/* ════════════════════════════════════════════
                    ── Applies To (Schedule) ──
                   ════════════════════════════════════════════ */}
                <div style={{ marginBottom: 20 }}>
                    <span className="spb-section-label">Applies To</span>

                    {/* Weekend-only toggle — restricts every mode below to Sat/Sun
                       instead of Mon-Fri (the backend now enforces this per-band,
                       never mixed — see is_weekend in the requirements doc). */}
                    <div style={{ margin: '8px 0 12px' }}>
                        <Checkbox checked={isWeekend} onChange={handleWeekendToggle}>
                            Weekend only (Saturday &amp; Sunday)
                        </Checkbox>
                    </div>

                    {/* Schedule type toggle */}
                    <div style={{ marginBottom: 12 }}>
                        <Radio.Group
                            value={scheduleType}
                            onChange={(e) => {
                                setScheduleType(e.target.value);
                                setScheduleError(null);
                            }}
                            buttonStyle="solid"
                            style={{ '--ant-color-primary': '#008385' }}
                        >
                            <Radio.Button value="days">Days of Week</Radio.Button>
                            <Radio.Button value="dates">Custom Dates</Radio.Button>
                            <Radio.Button value="date_range">Date Range</Radio.Button>
                        </Radio.Group>
                    </div>

                    {/* Days of Week — pill toggles */}
                    {scheduleType === 'days' && (
                        <div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                                {DAYS_OF_WEEK.map((d) => {
                                    const active = selectedDays.includes(d.value);
                                    const isWeekendValue = d.value === 6 || d.value === 7;
                                    const disabled = isWeekend ? !isWeekendValue : isWeekendValue;
                                    return (
                                        <button
                                            key={d.value}
                                            type="button"
                                            disabled={disabled}
                                            onClick={() => !disabled && toggleDay(d.value)}
                                            className={`day-pill${active ? ' day-pill--active' : ''}`}
                                        >
                                            {d.label}
                                        </button>
                                    );
                                })}
                            </div>
                            {scheduleError && (
                                <div className="spb-schedule-error">{scheduleError}</div>
                            )}
                        </div>
                    )}

                    {/* Custom Dates — DatePicker + tags */}
                    {scheduleType === 'dates' && (
                        <div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                                <DatePicker
                                    value={datePickerValue}
                                    onChange={(val) => setDatePickerValue(val)}
                                    disabledDate={buildScheduleDisabledDate(isWeekend)}
                                    format="DD MMM YYYY"
                                    placeholder="Select a date"
                                    style={{ width: 190 }}
                                />
                                <ConfigProvider theme={{ components: { Button: { colorPrimary: '#008385', colorPrimaryHover: '#006f71', colorPrimaryActive: '#006f71' } } }}>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        disabled={!datePickerValue}
                                        onClick={() => {
                                            if (!datePickerValue) return;
                                            const formatted = datePickerValue.format('YYYY-MM-DD');
                                            if (!selectedDates.includes(formatted)) {
                                                setSelectedDates((prev) => [...prev, formatted]);
                                                setScheduleError(null);
                                            }
                                            setDatePickerValue(null);
                                        }}
                                    >
                                        Add
                                    </Button>
                                </ConfigProvider>
                            </div>

                            {selectedDates.length === 0 ? (
                                <div style={{ color: '#bfbfbf', fontSize: 12, marginBottom: 4 }}>
                                    No dates added yet.
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 4, maxHeight: 132, overflowY: 'auto' }}>
                                    {selectedDates.map((date) => (
                                        <Tag
                                            key={date}
                                            closable
                                            onClose={() =>
                                                setSelectedDates((prev) => prev.filter((d) => d !== date))
                                            }
                                            style={{
                                                background: '#f0fafa',
                                                borderColor: '#008385',
                                                color: '#008385',
                                                borderRadius: 20,
                                                padding: '2px 10px',
                                                fontSize: 12,
                                            }}
                                        >
                                            <CalendarOutlined style={{ marginRight: 4 }} />
                                            {dayjs(date).format('DD MMM YYYY')}
                                        </Tag>
                                    ))}
                                </div>
                            )}
                            {scheduleError && (
                                <div className="spb-schedule-error">{scheduleError}</div>
                            )}
                        </div>
                    )}

                    {/* Date Range — a single continuous period, sent as schedule_type: 'date_range' */}
                    {scheduleType === 'date_range' && (
                        <div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                                <RangePicker
                                    value={scheduleDateRange}
                                    onChange={(vals) => { setScheduleDateRange(vals); setScheduleError(null); }}
                                    disabledDate={buildScheduleDisabledDate(isWeekend)}
                                    format="DD MMM YYYY"
                                    placeholder={['Start date', 'End date']}
                                    style={{ width: 260 }}
                                />
                            </div>
                            {scheduleDateRange?.[0] && scheduleDateRange?.[1] && (
                                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                                    Applies every day from {scheduleDateRange[0].format('D MMM YYYY')} to {scheduleDateRange[1].format('D MMM YYYY')}.
                                </div>
                            )}
                            {scheduleError && (
                                <div className="spb-schedule-error">{scheduleError}</div>
                            )}
                        </div>
                    )}
                </div>

                {/* ════════════════════════════════════════════
                    ── Time Ranges ──
                   ════════════════════════════════════════════ */}
                <div style={{ marginBottom: 16 }}>
                    <span className="spb-section-label">Time Ranges (Day-Time only)</span>
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12, fontSize: 12 }}
                        message={
                            <>
                                Start time minimum <strong>08:00</strong>. End time must be at or before{' '}
                                <strong>{bucket?.evening_price_starts_at?.slice(0, 5) || 'evening start'}</strong>{' '}
                                (Day-Time boundary).
                                {scheduleType === 'days' && selectedDays.length > 0 && (
                                    <>
                                        {' '}Applies on:{' '}
                                        <strong>
                                            {selectedDays
                                                .sort()
                                                .map((v) => DAYS_OF_WEEK.find((d) => d.value === v)?.label)
                                                .join(', ')}
                                        </strong>.
                                    </>
                                )}
                                {scheduleType === 'dates' && selectedDates.length > 0 && (
                                    <>
                                        {' '}Applies on{' '}
                                        <strong>{selectedDates.length} specific date(s)</strong>.
                                    </>
                                )}
                                {scheduleType === 'date_range' && scheduleDateRange?.[0] && scheduleDateRange?.[1] && (
                                    <>
                                        {' '}Applies every day from{' '}
                                        <strong>
                                            {scheduleDateRange[0].format('D MMM YYYY')} to {scheduleDateRange[1].format('D MMM YYYY')}
                                        </strong>.
                                    </>
                                )}
                            </>
                        }
                    />

                    {timeRanges.map((range, index) => (
                        <div key={index}>
                            <div className="time-range-row">
                                {/* START — min 08:00 */}
                                <TimePicker
                                    format="HH:mm"
                                    minuteStep={15}
                                    placeholder="Start"
                                    value={range.start_time ? dayjs(range.start_time, 'HH:mm') : null}
                                    onChange={(_, str) => updateTimeRange(index, 'start_time', str || null)}
                                    disabledTime={startDisabledTime}
                                    style={{ width: 130 }}
                                    status={timeRangeErrors[index] ? 'error' : undefined}
                                    showNow={false}
                                />
                                <span style={{ color: '#8c8c8c', fontWeight: 600 }}>→</span>
                                {/* END — max = evening boundary */}
                                <TimePicker
                                    format="HH:mm"
                                    minuteStep={15}
                                    placeholder="End"
                                    value={range.end_time ? dayjs(range.end_time, 'HH:mm') : null}
                                    onChange={(_, str) => updateTimeRange(index, 'end_time', str || null)}
                                    disabledTime={endDisabledTime}
                                    style={{ width: 130 }}
                                    status={timeRangeErrors[index] ? 'error' : undefined}
                                    showNow={false}
                                />
                                {timeRanges.length > 1 && (
                                    <MinusCircleOutlined
                                        className="time-range-remove-btn"
                                        onClick={() => removeTimeRange(index)}
                                    />
                                )}
                            </div>
                            {timeRangeErrors[index] && (
                                <div className="time-range-error-msg">{timeRangeErrors[index]}</div>
                            )}
                        </div>
                    ))}

                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={addTimeRange}
                        style={{ marginTop: 6, borderColor: '#008385', color: '#008385' }}
                        size="small"
                    >
                        Add Time Range
                    </Button>
                </div>

                {/* ── Form Buttons ── */}
                <div style={{
                    display: 'flex', justifyContent: 'flex-end', gap: 10,
                    marginTop: 8, paddingTop: 16, borderTop: '1px solid #f0f0f0',
                }}>
                    <Button htmlType="button" className="add-generic-btn" onClick={goToList} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        loading={saving}
                        disabled={saving}
                        onClick={handleSave}
                        style={{
                            background: saving ? '#80bebe' : '#008385',
                            borderColor: saving ? '#80bebe' : '#008385',
                            cursor: saving ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {saving ? 'Saving...' : 'Save Band'}
                    </Button>
                </div>
            </Form>
        </>
    );

    /* ── Modal ──────────────────────────────────────────────────────── */
    return (
        <>
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1040}
            destroyOnClose
            title={
                <div
                    className="spb-modal-header"
                    style={{ margin: '-20px -24px 0', padding: '14px 24px 12px' }}
                >
                    <div>
                        <div className="spb-modal-header-title">
                            <TagsOutlined style={{ color: '#008385', marginRight: 6 }} /> Special Pricing Bands
                        </div>
                        <div className="spb-modal-header-subtitle">
                            {durationLabel && <strong>{durationLabel} </strong>}
                            {bucket?.bucket_name ? `— ${bucket.bucket_name}` : ''}
                        </div>
                    </div>
                </div>
            }
            styles={{ body: { paddingTop: 20 } }}
        >
            {formMode === 'list' ? renderList() : renderForm()}
        </Modal>
        {modalContextHolder}
        </>
    );
};

export default SpecialPricingBandsModal;
